import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  useBulkCreateCreator,
  useBulkValidation,
  useFileValidationCreator
} from '@/hooks/queries/creators';
import useAlert from '@/hooks/useAlert';
import { Creator } from '@/models/creator';

const extractError = (response: any): string | string[] | null => {
  if (response?.data?.errors?.length) return response.data.errors;
  if (response?.data?.error) return response.data.error;
  if (response?.status === 422)
    return 'All accounts already exist in your workspace.';
  return null;
};

export const useImportCreators = (onClose: () => void) => {
  const alert = useAlert();
  const queryClient = useQueryClient();

  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileValidation, setFileValidation] =
    useState<Creator.FileValidation | null>(null);
  const [validationError, setValidationError] = useState<
    string | string[] | null
  >(null);
  const [bulkResult, setBulkResult] =
    useState<Creator.BulkValidationResponse | null>(null);

  const isValidated = bulkResult !== null;

  const hasErrors = !!validationError;
  const hasWarnings =
    fileValidation?.creators.some(
      ({ creatorWarnings }) =>
        (creatorWarnings?.wrong.length ?? 0) > 0 ||
        (creatorWarnings?.duplicates.length ?? 0) > 0
    ) ?? false;

  const { mutate: validateFile, isPending: isFileValidating } =
    useFileValidationCreator({
      onSuccess: ({ data }) => {
        setFileValidation(data);
        setValidationError(data.errors?.length ? data.errors : null);
        setActiveStep(1);
      },
      onError: ({ response }) => {
        setFileValidation(null);
        setValidationError(extractError(response));
        setActiveStep(1);
      }
    });

  const { mutate: validateBulk, isPending: isBulkValidating } =
    useBulkValidation({
      onError: ({ response }) => setValidationError(extractError(response))
    });

  const { mutate: createBulk, isPending: isBulkCreating } =
    useBulkCreateCreator({
      onSuccess: () => {
        alert.success('Creators imported successfully');
        queryClient.invalidateQueries({
          queryKey: ['creators'],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['creator'],
          refetchType: 'all'
        });
        onClose();
      }
    });

  const runBulkValidation = (
    payload: Creator.BulkCreatePayload,
    onSuccess?: () => void
  ) => {
    validateBulk(payload, {
      onSuccess: ({ data }) => {
        setBulkResult(data);
        setValidationError(null);
        onSuccess?.();
      }
    });
  };

  const handleContinue = () => {
    if (activeStep === 0) {
      if (!uploadedFile) return;
      const formData = new FormData();
      formData.append('file', uploadedFile);
      validateFile(formData);
      return;
    }

    if (activeStep === 1) {
      if (bulkResult) {
        setActiveStep(2);
        return;
      }
      const payload: Creator.BulkCreatePayload = {
        creators:
          fileValidation?.creators.map(({ name, accounts }) => ({
            name,
            accounts
          })) ?? []
      };
      runBulkValidation(payload, () => setActiveStep(2));
      return;
    }

    if (activeStep === 2 && bulkResult) {
      createBulk({ creators: bulkResult.creators });
    }
  };

  const handlePrevious = () => {
    if (activeStep === 1) {
      setActiveStep(0);
      setUploadedFile(null);
      setFileValidation(null);
      setValidationError(null);
      setBulkResult(null);
      return;
    }

    if (activeStep === 2) {
      setActiveStep(1);
      setBulkResult(null);
      setValidationError(
        fileValidation?.errors?.length ? fileValidation.errors : null
      );
    }
  };

  const isContinueDisabled =
    (activeStep === 0 && (!uploadedFile || isFileValidating)) ||
    (activeStep === 1 && (hasErrors || (hasWarnings && !isValidated)));

  return {
    activeStep,
    uploadedFile,
    setUploadedFile,
    fileValidation,
    validationError,
    isValidated,
    summary: bulkResult?.summary ?? null,
    handleValidate: runBulkValidation,
    isBulkValidating,
    handlePrevious,
    handleContinue,
    isContinueDisabled,
    isLoading: isFileValidating || isBulkValidating || isBulkCreating
  };
};
